import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { dealsApi } from '../../api/deals'
import { landscapeApi } from '../../api/landscape'
import { RefusedError } from '../../api/client'
import { ConnectGlyph } from '../../components/ConnectChecklist'
import { useLandscape } from '../../hooks/useLandscape'
import { useSession } from '../../hooks/useSession'
import { useSpoke } from '../../hooks/useSpoke'
import { scopeWords } from '../../lib/orgs'
import { GoalDialog } from './GoalDialog'
import { LandscapeMap } from './LandscapeMap'
import { SchedulePanel } from './SchedulePanel'
import { Today } from './Today'

// The Digital Landscape: the getting-started map. What Scout does, which of your systems are
// connected, the firm's goals and which systems each one needs, and the one next thing to do.
// Screens stay hidden until the system that feeds them is connected; the API decides, this page shows.
export function Landscape() {
  const { user } = useSession()
  const spoke = useSpoke()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const q = useLandscape()
  const [adding, setAdding] = useState(false)
  const scan = useMutation({ mutationFn: () => dealsApi.scan(spoke, 150), onSuccess: (r) => navigate(`/jobs/${encodeURIComponent(r.job.id)}`) })
  const remove = useMutation({
    mutationFn: (id: string) => landscapeApi.removeGoal(spoke, id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['landscape', spoke] }),
  })

  return (
    <section className="page landscape">
      {/* Today first: five meetings, five opportunities, the stalled ones red. It hides itself when
          there is nothing on file yet, so a first visit still opens on the step card. */}
      <Today />

      {/* The org the counts on this page are for. The step text below is the API's words, and it is where
          "nothing places you in an org yet" gets explained. */}
      <p className="kicker">Getting started{q.isSuccess && scopeWords(q.data.scope) ? ` \u00b7 ${scopeWords(q.data.scope)}` : ''}{q.isSuccess && q.data.viewer.isAdmin ? ' \u00b7 admin' : ''}</p>
      <h1>Digital Landscape</h1>
      <p className="lede">
        Scout puts a warm path on every opportunity before competitors see it. It does that by learning from the systems your firm already runs. Connect them one at a time;
        each one unlocks a part of the product and moves a goal within reach.
      </p>

      {q.isPending && <p className="muted">Loading…</p>}
      {q.isError && (
        <div className="refusal" role="status">
          <strong>{q.error instanceof RefusedError ? q.error.refusal.reason : 'Unavailable'}</strong>
          <p>{q.error.message}</p>
        </div>
      )}

      {q.isSuccess && (
        <>
          <div className="card step-card">
            <div>
              <p className="kicker">Step {q.data.step} of {q.data.steps}</p>
              <h2>{q.data.nextAction}</h2>
              <p className="muted">
                {q.data.step === 1 && `Start with your own mailbox, ${user?.displayName?.split(' ')[0] ?? 'there'}. ${q.data.systems.find((s) => s.id === 'mailbox')?.firmCount ? 'Others in the firm have connected theirs; nobody sees anyone else\u2019s. ' : ''}Scout reads only your sent items, keeps only the words you typed, and never sends anything.`}
                {q.data.step === 2 && (q.data.activeJobId ? 'Scout is reading your sent mail now: measuring how you write, per audience and intent. Watch it take shape, or come back when it lands.' : q.data.readState === 'stopped' ? 'The last read of your mailbox stopped before it finished. Resuming continues from where it left off; nothing is read twice.' : 'Your mailbox is connected. One read of your sent mail measures how you write, per audience and intent.')}
                {q.data.step === 3 && (q.data.activeJobId ? 'The model is reading your external threads: company, stage, amounts, proposals and SOWs out, next steps.' : 'Your voice is live. Next, have the model read your external threads: every lead and opportunity in your own mail, with the dollar amounts and the next step still open.')}
                {q.data.step >= 4 && 'Your pipeline is on the map. Connect the next system when your firm is ready; each one unlocks more.'}
              </p>
            </div>
            <div className="actions">
              {q.data.step === 1 && <Link className="button primary" to="/mailbox">Connect my mailbox</Link>}
              {q.data.step === 2 && (q.data.activeJobId ? <Link className="button primary" to={`/jobs/${encodeURIComponent(q.data.activeJobId)}`}>Watch the import</Link> : q.data.readState === 'stopped' ? <Link className="button primary" to="/mailbox">Resume the read</Link> : <Link className="button primary" to="/mailbox">Read my mailbox</Link>)}
              {q.data.step === 3 && (q.data.activeJobId ? <Link className="button primary" to={`/jobs/${encodeURIComponent(q.data.activeJobId)}`}>Watch the read</Link> : <button className="button primary" onClick={() => scan.mutate()} disabled={scan.isPending}>{scan.isPending ? 'Starting…' : 'Find my leads and opportunities'}</button>)}
              {q.data.step >= 4 && <Link className="button primary" to="/opportunities">Open Opportunities</Link>}
            </div>
          </div>

          <div className="card map-card">
            <LandscapeMap systems={q.data.systems} goals={q.data.goals} onOpen={(route) => navigate(route)} onAddGoal={() => setAdding(true)} onRemoveGoal={(id) => remove.mutate(id)} />
          </div>

          <SchedulePanel />

          <div className="two-up">
            <div className="card">
              <h2>Systems</h2>
              <ul className="systems">
                {q.data.systems.map((s, i) => {
                  const live = s.scope === 'firm' ? s.status === 'connected' : s.mine
                  return (
                    <li key={s.id} className={s.status === 'planned' ? 'planned' : live ? 'connected' : 'available'}>
                      <span className="step-no">{i + 1}</span>
                      <div>
                        {/* the same glyph the Connect menu draws, from the same field: the menu and this
                            panel can never disagree about whether a connector still opens */}
                        <ConnectGlyph health={s.health} size={15} /> <strong>{s.name}</strong>{' '}
                        <span className={`tag ${s.status}`}>
                          {s.status === 'planned' ? 'Coming' : s.scope === 'personal'
                            ? `${s.firmCount} connected in the firm · yours ${s.mine ? 'connected' : 'not yet'}`
                            : s.status === 'connected' ? 'Connected for the firm' : 'Available now'}
                        </span>
                        <span className="tag scope">{s.scope === 'personal' ? 'each person' : 'firm-wide · admins'}</span>
                        {s.healthReason && <p className="warn small">{s.healthReason}</p>}
                        <p className="muted">{s.blurb}</p>
                        <p className="muted small">Unlocks: {s.unlocks.map((id) => q.data.screens.find((sc) => sc.id === id)?.title ?? id).join(', ')}</p>
                        {s.enrollments && s.enrollments.length > 0 && (
                          <ul className="enrollments">
                            {s.enrollments.map((e) => (
                              <li key={e.userId}>
                                <strong>{e.displayName}</strong> <span className="muted">{e.address} · {e.messageCount} messages · {new Date(e.connectedAt).toLocaleDateString()}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      {s.route && s.canConnect ? (
                        <Link className="button small" to={s.route}>
                          {s.scope === 'personal' ? (s.mine ? 'Manage mine' : 'Connect mine') : live ? 'Manage' : 'Connect'}
                        </Link>
                      ) : s.scope === 'firm' && !s.canConnect ? (
                        <span className="muted small">Admins connect this</span>
                      ) : null}
                    </li>
                  )
                })}
              </ul>
            </div>
            <div className="card">
              <div className="actions space-between">
                <h2>Goals</h2>
                <button className="button small" onClick={() => setAdding(true)}>+ Add a goal</button>
              </div>
              <ul className="goals">
                {q.data.goals.map((g) => (
                  <li key={g.id} className={g.unlocked ? 'unlocked' : 'locked'}>
                    <div>
                      <strong>{g.title}</strong>
                      <span className={`tag ${g.unlocked ? 'live' : 'locked'}`}>{g.unlocked ? 'Within reach' : 'Needs ' + g.dependsOn.map((d) => q.data.systems.find((s) => s.id === d)?.name.toLowerCase() ?? d).join(' + ')}</span>
                      {g.detail && <p className="muted">{g.detail}</p>}
                    </div>
                    {g.custom && (
                      <button className="link danger" onClick={() => remove.mutate(g.id)} aria-label={`Remove goal ${g.title}`}>
                        Remove
                      </button>
                    )}
                  </li>
                ))}
              </ul>
              <h3>Screens</h3>
              <ul className="screens">
                {q.data.screens.map((sc) => (
                  <li key={sc.id} className={sc.unlocked ? 'unlocked' : 'locked'}>
                    <div>
                      <strong>{sc.unlocked ? <Link to={sc.route}>{sc.title}</Link> : sc.title}</strong>
                      <span className={`tag ${sc.status}`}>{sc.status === 'live' ? 'Live' : sc.status === 'live-with-gaps' ? 'Live, with gaps' : sc.status === 'building' ? 'Building' : sc.status === 'stopped' ? 'Read stopped' : sc.status === 'ready-to-build' ? 'Ready to build' : sc.status === 'locked' ? 'Locked' : 'Coming'}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          {adding && <GoalDialog systems={q.data.systems} onClose={() => setAdding(false)} />}
        </>
      )}
    </section>
  )
}
